import React, { useState } from 'react';
import { Header } from '../../../../components';
import { Button, DatePicker, Segmented, Alert, Empty, Space } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useStateContext } from '../../../../contexts/ContextProvider';
import { usePickupTimeReport } from '../hooks/useFetch';
import PickupTable from '../components/Table';

const { RangePicker } = DatePicker;

const fmt = (d) => d.format('YYYY-MM-DD');

/**
 * Raw Data > Pickup Time
 * เวลาตั้งแต่เคสเด้งเข้ากะ จนพนักงานกดเริ่มทำ — แจกแจงรายคนทีละ 5 วินาที ถึง 2 นาที
 */
function PickupTimeReport() {
  const { currentColor } = useStateContext();

  const [range, setRange] = useState([dayjs().subtract(6, 'day'), dayjs()]);
  // ยิงเมื่อกดค้นหาเท่านั้น — ช่วงวันที่ที่ "ส่งไปแล้ว" เก็บแยกจากช่องที่กำลังเลือก
  const [query, setQuery] = useState({ from: fmt(dayjs().subtract(6, 'day')), to: fmt(dayjs()) });

  const [cumulative, setCumulative] = useState('สะสม');
  const [unit, setUnit] = useState('จำนวน');

  const { data, isFetching, refetch } = usePickupTimeReport({
    from: query.from,
    to: query.to,
    enabled: true,
  });

  const handleSearch = () => {
    if (!range?.[0] || !range?.[1]) return;
    const next = { from: fmt(range[0]), to: fmt(range[1]) };
    if (next.from === query.from && next.to === query.to) refetch();
    else setQuery(next);
  };

  const hasRows = (data?.rows || []).length > 0;

  return (
    <div className="md:m-10" style={{ color: '#212121' }}>
      <Header category="Raw Data" title="Pickup Time" />

      <Alert
        type="info"
        showIcon
        className="mb-4"
        message="เวลาตั้งแต่เคสเด้งเข้ากะ จนพนักงานกดเริ่มทำ"
        description={
          <>
            นับเฉพาะช่วงที่พนักงาน<b>ว่างอยู่แล้วยังไม่กดรับ</b> —
            ถ้าตอนเคสเด้งเข้ามายังทำเคสอื่นค้างอยู่ จะเริ่มจับเวลาตอนที่ปิดเคสก่อนหน้าเสร็จ
            <br />
            ใช้เวลา “กด Start ครั้งแรก” จากประวัติเคส ไม่ใช่ครั้งล่าสุด —
            เคสที่ Pending แล้วกลับมาทำใหม่จึงไม่ถูกนับเวลารอซ้ำ
          </>
        }
      />

      <div className="flex gap-2 mb-4 flex-wrap items-center">
        <RangePicker
          value={range}
          onChange={setRange}
          format="DD/MM/YYYY"
          allowClear={false}
          disabledDate={(d) => d && d.isAfter(dayjs(), 'day')}
        />
        <Button
          type="primary"
          icon={<SearchOutlined />}
          onClick={handleSearch}
          loading={isFetching}
          style={{ backgroundColor: currentColor, borderColor: currentColor }}
        >
          ค้นหา
        </Button>

        <Space className="ml-auto" wrap>
          <Segmented
            value={cumulative}
            onChange={setCumulative}
            options={['สะสม', 'แยกช่วง']}
          />
          <Segmented value={unit} onChange={setUnit} options={['จำนวน', '%']} />
        </Space>
      </div>

      <div className="mb-3 text-xs text-gray-500">
        {cumulative === 'สะสม'
          ? 'คอลัมน์ “≤ 10 วิ” = จำนวนเคสที่กดรับภายใน 10 วินาทีนับจากตอนที่ว่าง (รวมที่เร็วกว่านั้นด้วย)'
          : 'คอลัมน์ “6-10 วิ” = จำนวนเคสที่กดรับในช่วง 6 ถึง 10 วินาที เท่านั้น'}
        {' · ถัดจาก 2 นาทีเป็นช่วงหลักชั่วโมง (คอลัมน์แดง)'}
      </div>

      {!isFetching && !hasRows ? (
        <Empty description="ไม่พบข้อมูลในช่วงที่เลือก" />
      ) : (
        <PickupTable
          data={data}
          loading={isFetching}
          cumulative={cumulative === 'สะสม'}
          showPercent={unit === '%'}
        />
      )}
    </div>
  );
}

export default PickupTimeReport;
